import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuStructure = {
    services: [
      { name: 'Specialised Investment Services', path: '/wealth-builder' },
      { name: 'Goal Planning', path: '/goal-planner' },
      { name: 'Retirement Planning', path: '/retirement' },
      { name: 'Insurance Plans', path: '/insurance' },
      { name: 'Tax Optimization', path: '/tax-optimization' },
      { name: 'NRI Connect', path: '/nri' },
    ],
    calculators: [
      { name: 'Goal Planner', path: '/calculators/goal-planning' },
      { name: 'Retirement Planner', path: '/calculators/financial-planning' },
      { name: 'Crorepati Calculator', path: '/calculators/crorepati' },
      { name: 'Emergency Fund Builder', path: '/calculators/emergency' },
      { name: 'SIP vs EMI', path: '/calculators/sip-emi' },
      { name: 'Portfolio Forecasting', path: '/calculators/portfolio-forecast' },
    ],
  };

  const navLinks = [
    { name: 'Services', path: '/services', dropdownKey: 'services' },
    { name: 'Calculators', path: '/calculators', dropdownKey: 'calculators' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-black/95 backdrop-blur-xl shadow-2xl py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center group">
            <img 
              src="https://customer-assets.emergentagent.com/job_dezerv-frontier/artifacts/gf02e7f8_Untitled%20%28500%20x%20200%20px%29%20%281%29.png" 
              alt="Frontier Investments" 
              className="h-20 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <div
                key={link.name}
                className="relative"
                onMouseEnter={() => setActiveDropdown(link.dropdownKey)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <Link
                  to={link.path}
                  className={`px-4 py-2 text-sm font-medium transition-all duration-300 rounded-lg flex items-center gap-1 ${
                    location.pathname.startsWith(link.path)
                      ? 'text-white bg-white/10'
                      : 'text-white/70 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {link.name}
                  <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${activeDropdown === link.dropdownKey ? 'rotate-180' : ''}`} />
                </Link>
                
                <div
                  className={`absolute top-full left-0 mt-2 w-64 bg-black/95 backdrop-blur-xl rounded-xl border border-white/10 shadow-2xl overflow-hidden transition-all duration-300 ${
                    activeDropdown === link.dropdownKey ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                  }`}
                >
                  <div className="py-2">
                    {menuStructure[link.dropdownKey]?.map((item) => (
                      <Link
                        key={item.name}
                        to={item.path}
                        className="block px-4 py-2.5 text-sm text-white/70 hover:text-white hover:bg-white/10 transition-colors duration-200"
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <Link
              to="/contact"
              className="text-white/70 hover:text-white text-sm font-medium transition-colors duration-300"
            >
              Contact Us
            </Link>
            <Link
              to="/portfolio-upload"
              data-testid="nav-portfolio-upload-btn"
              className="group relative px-6 py-2.5 bg-white text-black text-sm font-medium rounded-full overflow-hidden transition-all duration-300 hover:shadow-[0_0_30px_rgba(255,255,255,0.3)]"
            >
              <span className="relative z-10 flex items-center gap-2">
                Review My Portfolio
                <svg
                  className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </span>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 text-white hover:bg-white/10 rounded-lg transition-colors duration-300"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-500 ease-in-out ${
            isOpen ? 'max-h-[800px] opacity-100 mt-4' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-4 space-y-2">
            {navLinks.map((link) => (
              <div key={link.name}>
                <button
                  onClick={() => setActiveDropdown(activeDropdown === link.dropdownKey ? null : link.dropdownKey)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white transition-all duration-300"
                >
                  {link.name}
                  <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${activeDropdown === link.dropdownKey ? 'rotate-180' : ''}`} />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${activeDropdown === link.dropdownKey ? 'max-h-[400px]' : 'max-h-0'}`}>
                  <div className="pl-4 space-y-1 mt-2">
                    {menuStructure[link.dropdownKey]?.map((item) => (
                      <Link
                        key={item.name}
                        to={item.path}
                        onClick={() => setIsOpen(false)}
                        className="block px-4 py-2 rounded-lg text-sm text-white/60 hover:bg-white/10 hover:text-white transition-colors"
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ))}
            
            <Link
              to="/portfolio-upload"
              onClick={() => setIsOpen(false)}
              data-testid="nav-mobile-portfolio-btn"
              className="block px-4 py-3 bg-white text-black rounded-xl text-sm font-medium text-center mt-4"
            >
              Review My Portfolio
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
